import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@api/client';
import { useAuth } from '@features/auth/hooks/useAuth';
import { resolveHeaderApiError, type HeaderApiError } from './headerApiError';

export interface CustomPage {
  id: number;
  name: string;
  url: string;
  icon?: string;
  is_external: boolean;
  category: 'top_menu' | 'left_menu' | 'dashboard' | 'customer_card';
}

interface CustomPagesResponse {
  pages: CustomPage[];
}

// API функция
export async function fetchCustomPages(category: string): Promise<CustomPagesResponse> {
  const { data } = await apiClient.get<CustomPagesResponse>('/custom-pages', {
    params: { category },
  });
  return data;
}

export function useCustomPages(category: 'TOP_MENU' | 'LEFT_MENU' | 'DASHBOARD' = 'TOP_MENU') {
  const { user } = useAuth();
  // Client cache partition only. The backend derives employee authority from auth middleware.
  const cacheUserID = user?.id ?? 0;
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: customPagesKey(cacheUserID, category),
    queryFn: () => fetchCustomPages(category),
    enabled: cacheUserID > 0,
    staleTime: 600000, // 10 минут
    // Assignment revocation must be observed while the permanently mounted header stays open.
    refetchInterval: 60_000,
    retry: false,
  });

  const headerError: HeaderApiError | null = resolveHeaderApiError(error);

  return {
    pages: data?.pages || [],
    isLoading,
    isError,
    error: headerError,
    refetch,
  };
}

export const customPagesKey = (cacheUserID: number, category: string) =>
  ['custom-pages', cacheUserID, category] as const;
